'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { formatDateTime } from '@/lib/utils'
import {
  ArrowUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BriefcaseIcon,
  MailIcon,
  UserIcon,
  CalendarIcon,
  ShieldIcon,
  AlertCircleIcon,
  UsersIcon,
  MoreHorizontalIcon,
  EyeIcon,
  EditIcon,
  Trash2Icon,
  SquarePlusIcon,
  GlobeIcon,
  ImageIcon,
  SquareMenuIcon,
  ShieldUserIcon,
  MailCheckIcon,
  ShieldCheckIcon,
  ShieldOffIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { DeleteConfirmationDialog, UserAvatar, UsersFilters } from '@/components/partials'
import { ConfirmationDialog } from '@/components/partials'
import { SendEmailVerificationDialog } from '@/components/partials/users'
import { useUsers, useDeleteUser, useBulkDeleteUsers, useUpdateUser } from '@/hooks'
import { UserType, UsersFilterParams } from '@/types/user'
import { BulkDeleteResponse } from '@/types/general'
import { useUsersStore, useMeStore } from '@/store'
import { SortEnum } from '@/enums'
import { hasPermission } from '@/lib/permissions'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  const [sendVerificationUserId, setSendVerificationUserId] = useState<string | null>(null)
  const [approvalConfirm, setApprovalConfirm] = useState<{ userId: string; approved: boolean } | null>(null)

  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'USERS', 'CREATE')
  const canShow = hasPermission(currentUser, 'USERS', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'USERS', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'USERS', 'DELETE')
  const canSendVerification = canShow || canUpdate

  // User selection hooks from Zustand store
  const selectedUserIds = useUsersStore(state => state.selectedUserIds)
  const toggleUserSelection = useUsersStore(state => state.toggleUserSelection)
  const selectAllUsers = useUsersStore(state => state.selectAllUsers)
  const deselectAllUsers = useUsersStore(state => state.deselectAllUsers)
  const isUserSelected = useUsersStore(state => state.isUserSelected)

  // Bulk delete mutation from hook
  const { mutate: bulkDeleteUsersMutate, isPending: isBulkDeleting } = useBulkDeleteUsers()

  const filters = useMemo<UsersFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      blocked: params.get('blocked') ? params.get('blocked') === 'true' : undefined,
      approved: params.get('approved') ? params.get('approved') === 'true' : undefined,
      roles: (params.get('roles')?.split(',').filter(Boolean) as UsersFilterParams['roles']) || undefined,
      groups: (params.get('groups')?.split(',').filter(Boolean) as UsersFilterParams['groups']) || undefined,
      genders: (params.get('genders')?.split(',').filter(Boolean) as UsersFilterParams['genders']) || undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  const { data: usersData, isLoading, error, refetch, isFetching } = useUsers(filters)
  const { mutate: deleteUserMutate, isPending: isDeleting } = useDeleteUser()
  const { mutate: updateUserMutate } = useUpdateUser()

  // URL params handling
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const newFilters: UsersFilterParams = {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: (params.get('sortBy') as UsersFilterParams['sortBy']) || 'createdAt'
    }

    if (params.get('q')) {
      newFilters.q = params.get('q') || ''
    }
    if (params.get('blocked')) {
      newFilters.blocked = params.get('blocked') === 'true'
    }
    if (params.get('approved')) {
      newFilters.approved = params.get('approved') === 'true'
    }
    if (params.get('roles')) {
      newFilters.roles = params.get('roles')?.split(',').filter(Boolean) as UsersFilterParams['roles']
    }
    if (params.get('groups')) {
      newFilters.groups = params.get('groups')?.split(',').filter(Boolean) as UsersFilterParams['groups']
    }
    if (params.get('genders')) {
      newFilters.genders = params.get('genders')?.split(',').filter(Boolean) as UsersFilterParams['genders']
    }
    if (params.get('createdAtStart')) {
      newFilters.createdAtStart = params.get('createdAtStart') || undefined
    }
    if (params.get('createdAtEnd')) {
      newFilters.createdAtEnd = params.get('createdAtEnd') || undefined
    }
    if (params.get('updatedAtStart')) {
      newFilters.updatedAtStart = params.get('updatedAtStart') || undefined
    }
    if (params.get('updatedAtEnd')) {
      newFilters.updatedAtEnd = params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  // Update URL when filters change
  const updateUrlParamsManual = (newFilters: Partial<UsersFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<UsersFilterParams>) => {
    updateUrlParamsManual(newFilters)
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleResetFilters = () => {
    router.push('/users')
  }

  const handleSort = (sortBy: UsersFilterParams['sortBy']) => {
    const newSort = filters.sortBy === sortBy && filters.sort === SortEnum.ASC ? SortEnum.DESC : SortEnum.ASC
    handleFiltersChange({ sortBy, sort: newSort, page: 1 })
  }

  const handleViewUser = (userId: string) => {
    router.push(`/users/${userId}`)
  }

  const handleEditUser = (userId: string) => {
    router.push(`/users/${userId}/edit`)
  }

  const confirmDeleteUser = () => {
    if (deleteUserId) {
      deleteUserMutate(deleteUserId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['users'] })
          toast.success(t('deleted_successfully'))
          setDeleteUserId(null)
        },
        onError: error => {
          console.error('Error deleting user:', error)
          setDeleteUserId(null)
        }
      })
    }
  }

  const handleApproval = (userId: string, approved: boolean) => {
    setApprovalConfirm({ userId, approved })
  }

  const confirmApproval = () => {
    if (!approvalConfirm) {
      return
    }
    const { userId, approved } = approvalConfirm
    updateUserMutate(
      { userId, params: { approved } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['users'] })
          toast.success(approved ? t('user_approved_successfully') : t('user_approval_revoked'))
          setApprovalConfirm(null)
        },
        onError: () => {
          toast.error(t('something_went_wrong'))
          setApprovalConfirm(null)
        }
      }
    )
  }

  const handleBulkDelete = () => {
    if (selectedUserIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteUsers = () => {
    if (selectedUserIds.length > 0) {
      bulkDeleteUsersMutate(selectedUserIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['users'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (usersData && usersData.items.length > 0) {
      if (selectedUserIds.length === usersData.items.length) {
        // If all users are selected, deselect all
        deselectAllUsers()
      } else {
        // Otherwise, select all users
        selectAllUsers(usersData.items)
      }
    }
  }

  const renderActionItems = (user: UserType, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        {canShow && (
          <ItemComponent onClick={() => canShow && handleViewUser(user.id)}>
            <EyeIcon className="h-4 w-4" />
            {t('user_details')}
          </ItemComponent>
        )}

        {canUpdate && (
          <ItemComponent onClick={() => handleEditUser(user.id)}>
            <EditIcon className="h-4 w-4" />
            {t('edit_user')}
          </ItemComponent>
        )}
        {canSendVerification && !user.emailVerifiedAt && (
          <ItemComponent onClick={() => setSendVerificationUserId(user.id)}>
            <MailCheckIcon className="h-4 w-4" />
            {t('send_email_verification')}
          </ItemComponent>
        )}
        {canUpdate &&
          (user.approvedAt ? (
            <ItemComponent onClick={() => handleApproval(user.id, false)}>
              <ShieldOffIcon className="h-4 w-4" />
              {t('revoke_approval')}
            </ItemComponent>
          ) : (
            <ItemComponent onClick={() => handleApproval(user.id, true)}>
              <ShieldCheckIcon className="h-4 w-4" />
              {t('approve')}
            </ItemComponent>
          ))}
        {canDelete && (
          <ItemComponent onClick={() => setDeleteUserId(user.id)} className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  const getSortIcon = (column: string) => {
    if (filters.sortBy !== column) {
      return <ArrowUpDownIcon className="h-4 w-4" />
    }
    return filters.sort === SortEnum.ASC ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />
  }

  const getGenderBadgeColor = (gender: string) => {
    switch (gender) {
      case 'male':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-100'
      case 'female':
        return 'bg-pink-100 text-pink-800 hover:bg-pink-100'
      case 'diverse':
        return 'bg-purple-100 text-purple-800 hover:bg-purple-100'
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100'
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 hover:bg-red-100'
      case 'manager':
        return 'bg-orange-100 text-orange-800 hover:bg-orange-100'
      default:
        return 'bg-green-100 text-green-800 hover:bg-green-100'
    }
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertCircleIcon className="h-4 w-4" />
            <AlertDescription>{t('failed_to_load_records')}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <UsersIcon className="h-5 w-5" />
                {t('users')}
              </CardTitle>
              <CardDescription>{t('users_list_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/users/create">
                    <SquarePlusIcon />
                    <span className="hidden items-center gap-2 lg:flex">{t('create_user')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedUserIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedUserIds.length})</span>
                  </div>
                </Button>
              )}
              <UsersFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12.5 text-center">
                        <div className="flex items-center justify-center">
                          {canDelete && (
                            <Checkbox
                              checked={
                                usersData &&
                                usersData.items.length > 0 &&
                                selectedUserIds.length === usersData.items.length
                              }
                              onCheckedChange={() => {
                                handleSelectAll()
                              }}
                              onClick={e => {
                                e.stopPropagation()
                              }}
                              className="cursor-pointer"
                            />
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="w-12.5 text-center">
                        <ImageIcon className="mx-auto h-4 w-4" />
                      </TableHead>
                      <TableHead className="min-w-50">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('firstname')}
                          className="h-auto p-0 font-semibold hover:bg-transparent">
                          <UserIcon className="mr-2 h-4 w-4" />
                          {t('name')}
                          {getSortIcon('firstname')}
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-62.5">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('email')}
                          className="h-auto p-0 font-semibold hover:bg-transparent">
                          <MailIcon className="mr-2 h-4 w-4" />
                          {t('email')}
                          {getSortIcon('email')}
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-40">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('company')}
                          className="h-auto p-0 font-semibold hover:bg-transparent">
                          <BriefcaseIcon className="mr-2 h-4 w-4" />
                          {t('company')}
                          {getSortIcon('company')}
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-30 text-center">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('gender')}
                          className="h-auto p-0 font-semibold hover:bg-transparent">
                          <UserIcon className="mr-2 h-4 w-4" />
                          {t('gender')}
                          {getSortIcon('gender')}
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-37.5">
                        <ShieldIcon className="mr-2 inline h-4 w-4" />
                        {t('roles')}
                      </TableHead>
                      <TableHead className="min-w-37.5">
                        <ShieldUserIcon className="mr-2 inline h-4 w-4" />
                        {t('groups')}
                      </TableHead>
                      <TableHead className="min-w-50 text-center">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('timezone')}
                          className="h-auto p-0 font-semibold hover:bg-transparent">
                          <GlobeIcon className="mr-2 h-4 w-4" />
                          {t('timezone')}
                          {getSortIcon('timezone')}
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-50 text-center">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('approvedAt')}
                          className="h-auto p-0 font-semibold hover:bg-transparent">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {t('approved_at')}
                          {getSortIcon('approvedAt')}
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-50 text-center">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('createdAt')}
                          className="h-auto p-0 font-semibold hover:bg-transparent">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {t('created_at')}
                          {getSortIcon('createdAt')}
                        </Button>
                      </TableHead>
                      <TableHead className="min-w-50 text-center">
                        <Button
                          variant="ghost"
                          onClick={() => handleSort('updatedAt')}
                          className="h-auto p-0 font-semibold hover:bg-transparent">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {t('updated_at')}
                          {getSortIcon('updatedAt')}
                        </Button>
                      </TableHead>
                      <TableHead className="w-25 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <SquareMenuIcon className="h-4 w-4" />
                          {t('actions')}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!usersData?.items || usersData.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-muted-foreground py-8 text-center">
                          <UserIcon className="mx-auto mb-4 h-12 w-12 opacity-50" />
                          {t('no_results')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      usersData.items.map((user: UserType, index: number) => (
                        <ContextMenu key={user.id}>
                          <ContextMenuTrigger asChild>
                            <TableRow
                              className={`cursor-pointer transition-colors ${
                                index % 2 === 0
                                  ? 'dark:bg-background dark:hover:bg-muted/50 bg-white hover:bg-gray-50'
                                  : 'dark:bg-muted/25 dark:hover:bg-muted/50 bg-gray-50 hover:bg-gray-100'
                              }`}
                              onClick={e => {
                                const target = e.target as Element
                                if (
                                  target !== e.currentTarget &&
                                  (target.closest('[role="button"]') ||
                                    target.closest('[data-radix-collection-item]') ||
                                    target.closest('[data-radix-popper-content-wrapper]') ||
                                    target.closest('.dropdown-menu-trigger') ||
                                    target.tagName === 'BUTTON' ||
                                    target.closest('button'))
                                ) {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  return
                                }
                                handleViewUser(user.id)
                              }}>
                              {/* ...existing cells... */}
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center">
                                  {canDelete && (
                                    <Checkbox
                                      checked={isUserSelected(user.id)}
                                      onCheckedChange={() => toggleUserSelection(user.id)}
                                      onClick={e => {
                                        e.stopPropagation()
                                      }}
                                      className="cursor-pointer"
                                    />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center">
                                  <UserAvatar user={user} size="sm" />
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">
                                <div className="flex flex-col">
                                  <span>{user.fullName}</span>
                                  <span className="text-muted-foreground text-xs">ID: {user.id}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span>{user.email}</span>
                                  {user.emailVerifiedAt && (
                                    <Badge variant="outline" className="text-xs">
                                      {t('verified')}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm">
                                {user.company || <span className="text-muted-foreground">-</span>}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className={getGenderBadgeColor(user.gender)}>
                                  {t(user.gender)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {user.roles.map((role: string) => (
                                    <Badge key={role} variant="outline" className={getRoleBadgeColor(role)}>
                                      {t(role)}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {user.groups.map(group => (
                                    <Badge key={group.id} variant="outline">
                                      {group.name}
                                    </Badge>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-center text-sm">
                                {user.timezone}
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="text-sm">
                                  {user.approvedAt ? (
                                    <span className="text-muted-foreground">
                                      {formatDateTime(user.approvedAt, locale)}
                                    </span>
                                  ) : (
                                    <Badge
                                      variant="outline"
                                      className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                                      {t('pending_approval')}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="text-muted-foreground text-sm">
                                  <span>{formatDateTime(user.createdAt, locale)}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="text-muted-foreground text-sm">
                                  <span>{formatDateTime(user.updatedAt, locale)}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                      }}>
                                      <span className="sr-only">{t('open_menu')}</span>
                                      <MoreHorizontalIcon className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">{renderActionItems(user, true)}</DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          </ContextMenuTrigger>
                          <ContextMenuContent>{renderActionItems(user, false)}</ContextMenuContent>
                        </ContextMenu>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {usersData && (
                <DataTablePagination
                  currentPage={usersData.page}
                  totalPages={usersData.pages}
                  totalItems={usersData.total}
                  pageSize={usersData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={deleteUserId !== null}
          onCancel={() => setDeleteUserId(null)}
          onConfirm={confirmDeleteUser}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteUsers}
          isDeleting={isBulkDeleting}
          description={t('bulk_delete_confirmation_message')}
        />
      )}

      {sendVerificationUserId && (
        <SendEmailVerificationDialog
          userId={sendVerificationUserId}
          open={true}
          onOpenChange={open => !open && setSendVerificationUserId(null)}
        />
      )}

      <ConfirmationDialog
        open={approvalConfirm !== null}
        onCancel={() => setApprovalConfirm(null)}
        onConfirm={confirmApproval}
        description={approvalConfirm?.approved ? t('approve_confirmation') : t('revoke_approval_confirmation')}
      />
    </>
  )
}

export default List
